import React, { useEffect, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Chip,
    ListSubheader,
    TextField,
    Typography,
} from "@mui/material";
import LoadingDots from "./LoadingDots";
import { ds } from "../../styles/designSystem";

/**
 * 공통 자동완성/드롭다운 — MUI Autocomplete 의 사내 표준 래퍼.
 *
 * <h3>왜 필요한가</h3>
 * 거래처·메일 수신자·직원/부서 필터 등 검색형 입력이 6~7곳에 흩어져 있고, 각자
 * 디바운스·로딩 표기·칩 스타일·드롭다운 위치를 다르게 구현해 왔다. 본 컴포넌트는
 * 그 패턴을 단일 API 로 통합하기 위한 표준이다.
 *
 * <h3>핵심 차별점</h3>
 * <ul>
 *   <li><b>드롭다운은 항상 input 아래</b> — Popper flip 모디파이어를 끔으로써
 *       위로 뒤집히지 않게 강제한다 ({@code disableFlip=true}, 기본 on).</li>
 *   <li><b>"Loading..." 텍스트 없음</b> — MUI 기본 문구 대신 {@link LoadingDots} 의
 *       스피너 + 점 애니메이션만 보여준다.</li>
 *   <li>비동기 fetcher 모드 — {@code fetcher(q)} 만 넘기면 디바운스/로딩/dedupe 까지
 *       자동 처리.</li>
 * </ul>
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code multiple, freeSolo} — MUI 와 동일</li>
 *   <li>{@code value, onChange(newValue, reason, detail)}</li>
 *   <li>{@code options} 또는 {@code fetcher(q) => Promise<array>} — 둘 중 하나</li>
 *   <li>{@code debounceMs=200}, {@code fetchOnOpen=true}, {@code fetcherDeps=[]}</li>
 *   <li>{@code getOptionLabel, isOptionEqualToValue, groupBy} — MUI 와 동일</li>
 *   <li>{@code secondaryText(option)} — 기본 renderOption 의 둘째 줄 텍스트</li>
 *   <li>{@code renderOption, renderTag, renderGroup} — 필요 시 커스텀</li>
 *   <li>{@code validate(text) → string|null} — freeSolo 커밋 시 에러 메시지 반환</li>
 *   <li>{@code onCommitInvalid(text, error)} — 검증 실패 시 콜백 (notify 등)</li>
 *   <li>{@code variant="outlined"|"standard"}, {@code disableUnderline}</li>
 *   <li>{@code disableFlip=true} — false 로 두면 viewport 에 맞춰 위로 뒤집힐 수 있음</li>
 * </ul>
 */
export default function AppAutocomplete({
    // mode
    multiple = false,
    freeSolo = false,

    // value
    value,
    onChange,
    inputValue: inputValueProp,
    onInputChange,

    // data source
    options: staticOptions = [],
    fetcher,
    fetcherDeps = [],
    debounceMs = 500,
    fetchOnOpen = true,

    // identity
    getOptionLabel = (o) => (typeof o === "string" ? o : (o?.name || "")),
    getOptionKey,
    isOptionEqualToValue,
    groupBy,

    // render
    renderOption,
    renderTag,
    renderGroup,
    secondaryText,

    // input
    label,
    placeholder,
    helperText,
    autoFocus = false,
    size = "small",
    variant = "outlined",
    disableUnderline = false,
    startAdornment,
    fullWidth = true,
    /** 내부 TextField 의 sx 오버라이드 — 외부 페이지(키비주얼 등) 의 inputSoft 같은
     *  도메인 톤을 적용할 때 사용. AppAutocomplete 의 sx 는 wrapper(Autocomplete) 에 적용되어
     *  TextField 톤(테두리·배경·radius)을 바꾸지 못하므로 별도 prop 으로 분리. */
    textFieldSx,

    // ux
    noOptionsText = "검색 결과가 없습니다",
    emptyText = "검색어를 입력하세요",
    validate,
    onCommitInvalid,
    onKeyDown,

    // popper
    dropdownMaxHeight = 320,
    disableFlip = true,

    // misc
    sx,
    disabled = false,
    ...rest
}) {
    const [innerInput, setInnerInput] = useState("");
    const inputValue = inputValueProp ?? innerInput;
    const updateInput = (v) => {
        if (onInputChange) onInputChange(v);
        else setInnerInput(v);
    };

    const [asyncOptions, setAsyncOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);

    const isAsync = typeof fetcher === "function";
    const options = isAsync ? asyncOptions : staticOptions;

    /* 디바운스 fetch — open 일 때만, 빈 입력은 fetchOnOpen 에 따름 */
    useEffect(() => {
        if (!isAsync || !open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = (inputValue || "").trim();
        if (!q && !fetchOnOpen) {
            setAsyncOptions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await fetcher(q);
                setAsyncOptions(Array.isArray(res) ? res : []);
            } catch {
                setAsyncOptions([]);
            } finally {
                setLoading(false);
            }
        }, debounceMs);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, open, isAsync, ...fetcherDeps]);

    /* freeSolo 커밋 검증 — onChange reason 이 createOption / blur 일 때 마지막 입력값 검사 */
    const handleChange = (_, newVal, reason, detail) => {
        if (freeSolo && (reason === "createOption" || reason === "blur") && validate) {
            const last = multiple ? newVal[newVal.length - 1] : newVal;
            const t = typeof last === "string" ? last.trim() : null;
            if (t) {
                const err = validate(t);
                if (err) {
                    onCommitInvalid?.(t, err);
                    return;
                }
            }
        }
        onChange?.(newVal, reason, detail);
    };

    /* 기본 renderOption — main + sub 두 줄 */
    const defaultRenderOption = (props, option) => {
        const { key, ...restProps } = props;
        const k = getOptionKey
            ? getOptionKey(option)
            : (typeof option === "string" ? option : (option?.id ?? getOptionLabel(option)));
        const main = getOptionLabel(option);
        const sub = secondaryText?.(option);
        return (
            <Box
                component="li"
                key={k}
                {...restProps}
                sx={{
                    py: 0.6,
                    px: 1.2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0,
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: ds.colors.textPrimary, lineHeight: 1.35 }}>
                    {main}
                </Typography>
                {sub && (
                    <Typography sx={{ fontSize: 11, color: ds.colors.secondary, lineHeight: 1.35 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        );
    };

    /* 기본 renderGroup — 회색 톤 ListSubheader */
    const defaultRenderGroup = (params) => (
        <li key={params.key}>
            {params.group && (
                <ListSubheader
                    sx={{
                        lineHeight: "22px",
                        fontSize: 11,
                        fontWeight: 700,
                        color: ds.colors.secondary,
                        bgcolor: "#f8fafc",
                    }}
                >
                    {params.group}
                </ListSubheader>
            )}
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
    );

    /* 기본 renderTag — primaryBg 칩 */
    const defaultRenderTag = (val, index, getTagProps) => {
        const lbl = typeof val === "string" ? val : getOptionLabel(val);
        return (
            <Chip
                key={index}
                label={lbl}
                size="small"
                {...getTagProps({ index })}
                sx={{
                    fontSize: 11,
                    height: 22,
                    fontWeight: 500,
                    bgcolor: ds.colors.primaryBg,
                    color: ds.colors.primaryText,
                    "& .MuiChip-deleteIcon": { color: ds.colors.primaryText },
                }}
            />
        );
    };

    const popperModifiers = disableFlip
        ? [{ name: "flip", enabled: false }]
        : undefined;

    return (
        <Autocomplete
            multiple={multiple}
            freeSolo={freeSolo}
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            value={value}
            inputValue={inputValue}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onInputChange={(_, v) => updateInput(v)}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            options={options}
            loading={isAsync ? loading : false}
            filterOptions={isAsync ? (x) => x : undefined}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            groupBy={groupBy}
            noOptionsText={
                isAsync && !inputValue?.trim() && !fetchOnOpen ? emptyText : noOptionsText
            }
            loadingText={<LoadingDots compact label="" sx={{ py: 1.5 }} />}
            renderOption={renderOption || defaultRenderOption}
            renderGroup={groupBy ? (renderGroup || defaultRenderGroup) : undefined}
            renderTags={
                multiple
                    ? (values, getTagProps) =>
                        values.map((v, i) => (renderTag || defaultRenderTag)(v, i, getTagProps))
                    : undefined
            }
            slotProps={{
                /* 팝업(paper)·항목 hover/선택색·글자크기 등 드롭다운 "디자인"은
                 * styles/muiTheme.js 의 MuiAutocomplete styleOverrides 가 전담한다(공통 dropdownPaper/Item).
                 * 여기 slotProps 에는 이 컴포넌트만의 "동작/구조"(팝업 flip 끔, 2줄 옵션 좌측 정렬)만 남긴다. */
                popper: {
                    placement: "bottom-start",
                    ...(popperModifiers ? { modifiers: popperModifiers } : {}),
                },
                listbox: {
                    sx: {
                        maxHeight: dropdownMaxHeight,   // 이 컴포넌트는 호출부에서 높이를 조절할 수 있어 남긴다
                        /* MUI v7 의 listbox 슬롯이 옵션에 alignItems:center 를 descendant selector 로 박아둠.
                         * 우리 renderOption 들은 flexDirection:column(2줄) 을 쓰기 때문에 cross-axis 가 가로가 되어
                         * 텍스트가 가운데로 밀려버림. 동일 specificity 의 descendant selector 로 덮어쓴다. */
                        "& .MuiAutocomplete-option": {
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            textAlign: "left",
                        },
                    },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    label={label || undefined}
                    placeholder={placeholder}
                    helperText={helperText}
                    variant={variant}
                    sx={textFieldSx}
                    InputProps={{
                        ...params.InputProps,
                        ...(disableUnderline ? { disableUnderline: true } : {}),
                        sx: { fontSize: 13, gap: 0.5, ...(params.InputProps.sx || {}) },
                        startAdornment: startAdornment ? (
                            <>
                                {startAdornment}
                                {params.InputProps.startAdornment}
                            </>
                        ) : params.InputProps.startAdornment,
                    }}
                />
            )}
            sx={{ ...sx }}
            {...rest}
        />
    );
}
